import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { ICONS } from '../constants';
import { API_CONFIG } from '../config';
import { ViewMode, isClientRole } from '../types';
import ContextMenu, { ContextMenuItem } from './ContextMenu';
import { getCurrencySymbol } from '../utils/currency';
import RenameBiddingModal from './RenameBiddingModal';

interface BiddingSheet {
  id: string;
  name: string;
  projectId?: string;
  projectName?: string;
  currency?: string;
  videoStatus?: 'draft' | 'processing' | 'completed' | 'failed';
  status?: 'draft' | 'sent' | 'approved' | 'rejected' | 'hidden'; // Approval status
  revisionStatus?: 'pending' | 'rejected' | null;
  createdAt?: string;
  updatedAt?: string;
  videoUrl?: string;
  shotCount?: number;
  totalHours?: number;
  totalCost?: number;
  valueColumnMode?: 'hours' | 'sum';
  producerPercentage?: number;
  producerFixedFee?: number;
  useProducerFixedFee?: boolean;
  coordinatorFee?: number;
  coordinatorPercentage?: number;
  useCoordinatorFixedFee?: boolean;
  supervisorFee?: number;
  supervisorPercentage?: number;
  useSupervisorFixedFee?: boolean;
  finderFee?: number;
  finderPercentage?: number;
  useFinderFixedFee?: boolean;
  vatRate?: number;
  totalNet?: number;
  totalVat?: number;
  totalGross?: number;
  isRevenueSheet?: boolean; // TRUE for revenue (income) sheets, FALSE for expense sheets
  expenseSheetId?: string; // Reference to expense sheet (for revenue sheets)
  revenueSheetId?: string; // Reference to revenue sheet (for expense sheets)
  thumbnails?: string[];
  createdByName?: string;
  projectPrepaymentType?: 'PERCENT' | 'FIXED';
  projectPrepaymentValue?: number;
  projectBudget?: number;
  projectTotalPaid?: number;
}

interface BiddingListProps {
  biddingSheets: BiddingSheet[];
  currentRole: string | null;
  currentUserId?: string;
  onEditBidding: (bidding: BiddingSheet) => void;
  onDeleteBidding: (biddingId: string) => void;
  onDuplicateBidding: (biddingId: string) => void;
  onRenameBidding: (biddingId: string, newName: string) => void;
  onExportBidding: (biddingId: string, mode?: 'revenue' | 'expense' | 'all') => void;
  onCreateBidding: (mode?: 'video' | 'manual') => void;
  onUpdateBiddingStatus?: (biddingId: string, status: string) => void;
  viewMode: ViewMode;
}

const BiddingList: React.FC<BiddingListProps> = ({
  biddingSheets,
  currentRole,
  currentUserId,
  onEditBidding,
  onDeleteBidding,
  onDuplicateBidding,
  onRenameBidding,
  onExportBidding,
  onCreateBidding,
  onUpdateBiddingStatus,
  viewMode
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Multi-select state
  const [selectedBiddingIds, setSelectedBiddingIds] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  
  // Rename modal state
  const [renameModal, setRenameModal] = useState<{ isOpen: boolean; biddingId: string; currentName: string }>({
    isOpen: false,
    biddingId: '',
    currentName: ''
  });
  
  const handleBiddingClick = (e: React.MouseEvent, bidding: BiddingSheet, index: number) => {
    // Handle multi-select with Shift or Ctrl/Cmd
    if (e.shiftKey && lastSelectedIndex !== null) {
      // Range selection with Shift
      e.preventDefault();
      e.stopPropagation();
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const rangeIds = biddingSheets.slice(start, end + 1).map(b => b.id);
      setSelectedBiddingIds(prev => {
        const newSet = new Set(prev);
        rangeIds.forEach(id => newSet.add(id));
        return newSet;
      });
      setLastSelectedIndex(index);
      return;
    }
    
    if (e.ctrlKey || e.metaKey) {
      // Toggle selection with Ctrl/Cmd
      e.preventDefault();
      e.stopPropagation();
      setSelectedBiddingIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(bidding.id)) {
          newSet.delete(bidding.id);
        } else {
          newSet.add(bidding.id);
        }
        return newSet;
      });
      setLastSelectedIndex(index);
      return;
    }
    
    // Single click - only select/deselect, don't open
    setSelectedBiddingIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bidding.id)) {
        newSet.delete(bidding.id);
      } else {
        // Clear other selections and select only this one
        return new Set([bidding.id]);
      }
      return newSet;
    });
    setLastSelectedIndex(index);
  };
  
  const handleBiddingDoubleClick = (bidding: BiddingSheet) => {
    // Double click - open bidding sheet
    handleEditBidding(bidding);
  };
  
  const handleEditBidding = (bidding: BiddingSheet) => {
    // Call callback first to set selected bidding sheet immediately
    if (onEditBidding) {
      onEditBidding(bidding);
    }
    // Then navigate to bidding sheet URL
    navigate(`/bidding/${bidding.id}`);
  };
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; bidding: BiddingSheet } | null>(null);
  const [generalContextMenu, setGeneralContextMenu] = useState<{ x: number; y: number } | null>(null);

  // Calculate grand total (subtotal + all management fees)
  // Use same logic as ShotTable - if useFixedFee is true, use saved fee, otherwise calculate from percentage
  const calculateGrandTotal = (bidding: BiddingSheet): number => {
    const subtotal = Number(bidding.totalCost) || 0;
    
    const producerFee = bidding.useProducerFixedFee 
      ? (Number(bidding.producerFixedFee) || 0)
      : subtotal * ((Number(bidding.producerPercentage) || 0) / 100);
      
    const coordFee = bidding.useCoordinatorFixedFee
      ? (Number(bidding.coordinatorFee) || 0)
      : subtotal * ((Number(bidding.coordinatorPercentage) || 0) / 100);
      
    const supFee = bidding.useSupervisorFixedFee
      ? (Number(bidding.supervisorFee) || 0)
      : subtotal * ((Number(bidding.supervisorPercentage) || 0) / 100);
      
    const findFee = bidding.useFinderFixedFee
      ? (Number(bidding.finderFee) || 0)
      : subtotal * ((Number(bidding.finderPercentage) || 0) / 100);
      
    const grandTotal = subtotal + producerFee + coordFee + supFee + findFee;
    return Math.round(grandTotal * 100) / 100;
  };
  
  // Calculate total for selected bidding sheets
  const calculateSelectedTotal = (): { total: number; currency: string; totalShots: number } => {
    if (selectedBiddingIds.size === 0) {
      return { total: 0, currency: 'USD', totalShots: 0 };
    }
    
    let total = 0;
    let currency = 'USD';
    let totalShots = 0;
    
    selectedBiddingIds.forEach(id => {
      const bidding = biddingSheets.find(b => b.id === id);
      if (bidding) {
        total += calculateGrandTotal(bidding);
        if (bidding.currency) {
          currency = bidding.currency;
        }
        totalShots += Number(bidding.shotCount) || 0;
      }
    });
    
    return { total: Math.round(total * 100) / 100, currency, totalShots };
  };
  
  const selectedTotal = calculateSelectedTotal();
  
  // Clear selection on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedBiddingIds.size > 0) {
        setSelectedBiddingIds(new Set());
        setLastSelectedIndex(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedBiddingIds.size]);

  // Close context menu on click
  useEffect(() => {
    const handleClick = () => {
      setContextMenu(null);
      setGeneralContextMenu(null);
    };
    if (contextMenu || generalContextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu, generalContextMenu]);

  const handleContextMenu = (e: React.MouseEvent, bidding: BiddingSheet) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, bidding });
    setGeneralContextMenu(null);
  };

  const handleGeneralContextMenu = (e: React.MouseEvent) => {
    // Only show if right-click on background, not on tiles
    if ((e.target as HTMLElement).closest('[data-bidding-tile]')) {
      return;
    }
    e.preventDefault();
    setGeneralContextMenu({ x: e.clientX, y: e.clientY });
    setContextMenu(null);
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'processing':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'failed':
        return 'bg-red-500/20 text-red-400 border-red-500/50';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'completed':
        return <ICONS.Check className="w-4 h-4" />;
      case 'processing':
        return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400" />;
      case 'failed':
        return <ICONS.X className="w-4 h-4" />;
      default:
        return <ICONS.Clock className="w-4 h-4" />;
    }
  };

  // Expense sheet functionality removed - expense table is now a virtual view

  const getContextMenuItems = (bidding: BiddingSheet): ContextMenuItem[] => {
    const canEdit = currentRole === 'Super Admin' || currentRole === 'Producer' || currentRole === 'Project Manager';
    const canDelete = currentRole === 'Super Admin' || currentRole === 'Admin';
    const isClient = isClientRole(currentRole);

    const exportMenuItem: ContextMenuItem = isClient 
      ? {
          label: t('contextMenu.exportToExcel'),
          icon: <ICONS.Export className="w-4 h-4" />,
          onClick: () => onExportBidding(bidding.id, 'revenue'),
        }
      : {
          label: t('contextMenu.exportToExcel'),
          icon: <ICONS.Export className="w-4 h-4" />,
          onClick: () => {},
          subMenu: [
            {
              label: t('contextMenu.exportRevenue') || 'Revenue',
              onClick: () => onExportBidding(bidding.id, 'revenue'),
            },
            {
              label: t('contextMenu.exportExpense') || 'Expense',
              onClick: () => onExportBidding(bidding.id, 'expense'),
            },
            {
              label: t('contextMenu.exportAll') || 'All',
              onClick: () => onExportBidding(bidding.id, 'all'),
            },
          ]
      };

    const isHidden = bidding.status === 'hidden';

    return [
      {
        label: t('contextMenu.edit'),
        icon: <ICONS.Edit className="w-4 h-4" />,
        onClick: () => handleEditBidding(bidding),
        disabled: !canEdit,
      },
      {
        label: t('contextMenu.rename'),
        icon: <ICONS.Edit className="w-4 h-4" />,
        onClick: () => {
          setRenameModal({
            isOpen: true,
            biddingId: bidding.id,
            currentName: bidding.name
          });
        },
        disabled: !canEdit,
      },
      {
        label: t('contextMenu.duplicate'),
        icon: <ICONS.Copy className="w-4 h-4" />,
        onClick: () => onDuplicateBidding(bidding.id),
        disabled: !canEdit,
      },
      exportMenuItem,
      {
        separator: true,
        label: '',
        onClick: () => {},
      },
      {
        label: t('contextMenu.setStatus') || 'Установить статус',
        icon: <ICONS.Settings className="w-4 h-4" />,
        onClick: () => {},
        disabled: !canEdit,
        subMenu: [
          {
            label: `${bidding.status === 'draft' ? '✓ ' : ''}${t('bidding.statuses.draft') || 'Черновик'}`,
            onClick: () => onUpdateBiddingStatus?.(bidding.id, 'draft'),
          },
          {
            label: `${bidding.status === 'sent' ? '✓ ' : ''}${t('bidding.statuses.sent') || 'На согласовании'}`,
            onClick: () => onUpdateBiddingStatus?.(bidding.id, 'sent'),
          },
          {
            label: `${bidding.status === 'approved' ? '✓ ' : ''}${t('bidding.statuses.approved') || 'Утверждён'}`,
            onClick: () => onUpdateBiddingStatus?.(bidding.id, 'approved'),
          },
          {
            label: `${bidding.status === 'rejected' ? '✓ ' : ''}${t('bidding.statuses.rejected') || 'Отклонён'}`,
            onClick: () => onUpdateBiddingStatus?.(bidding.id, 'rejected'),
          },
          {
            separator: true,
            label: '',
            onClick: () => {},
          },
          {
            label: `${bidding.status === 'hidden' ? '✓ ' : ''}${t('bidding.statuses.hidden') || 'Скрыт'}`,
            onClick: () => onUpdateBiddingStatus?.(bidding.id, 'hidden'),
          },
        ],
      },
      {
        separator: true,
        label: '',
        onClick: () => {},
      },
      {
        label: t('contextMenu.delete'),
        icon: <ICONS.Trash className="w-4 h-4" />,
        onClick: () => onDeleteBidding(bidding.id),
        disabled: !canDelete,
        danger: true,
      },
    ];
  };

  if (biddingSheets.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <ICONS.Calculator className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p className="text-xl">{t('bidding.noBiddingSheets')}</p>
        <p className="text-sm mt-2">{t('bidding.createFirstBidding')}</p>
      </div>
    );
  }

  // List view
  if (viewMode === ViewMode.LIST) {
    return (
      <>
        <div className="flex flex-col space-y-2 p-6" onContextMenu={handleGeneralContextMenu}>
          {biddingSheets.map((bidding, index) => {
            const isSelected = selectedBiddingIds.has(bidding.id);
            const isHidden = bidding.status === 'hidden';
            return (
            <div
              key={bidding.id}
              data-bidding-tile="true"
              className={`bg-gray-800/50 border rounded-lg p-4 transition-all cursor-pointer flex items-center gap-4 relative ${
                isSelected 
                  ? 'border-indigo-500 bg-indigo-900/20 ring-2 ring-indigo-500/50' 
                  : 'border-gray-700 hover:border-indigo-500/50'
              } ${isHidden ? 'opacity-40 grayscale hover:opacity-70 hover:grayscale-0' : ''}`}
              onClick={(e) => handleBiddingClick(e, bidding, index)}
              onDoubleClick={() => handleBiddingDoubleClick(bidding)}
              onContextMenu={(e) => handleContextMenu(e, bidding)}
            >
              {/* First thumbnail */}
              {bidding.thumbnails && bidding.thumbnails.length > 0 ? (
                <img
                  src={bidding.thumbnails[0].startsWith('http') ? bidding.thumbnails[0] : `${API_CONFIG.BASE_URL}${bidding.thumbnails[0]}`}
                  alt={bidding.name}
                  className="w-20 h-12 object-cover rounded"
                  loading="lazy"
                />
              ) : (
                <div className="w-20 h-12 bg-gray-900 rounded flex items-center justify-center">
                  <ICONS.Camera className="w-5 h-5 text-gray-600" />
                </div>
              )}
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-white truncate">{bidding.name}</h3>
                <div className="flex gap-4 text-sm text-gray-400 mt-1">
                  {bidding.projectName && (
                    <span className="flex items-center gap-1">
                      <ICONS.Briefcase className="w-3 h-3" />
                      {bidding.projectName}
                    </span>
                  )}
                  {bidding.shotCount !== undefined && (
                    <span className="flex items-center gap-1">
                      <ICONS.Camera className="w-3 h-3" />
                      {bidding.shotCount} {t('bidding.shots')}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Video Status */}
              {bidding.videoStatus && (!bidding.status || bidding.status === 'draft') && (
                <div className={`px-3 py-1 rounded-full border text-xs font-medium ${getStatusColor(bidding.videoStatus)}`}>
                  {t(`bidding.status.${bidding.videoStatus}`) || bidding.videoStatus}
                </div>
              )}
              {bidding.revisionStatus === 'pending' && (
                <div className="px-3 py-1 rounded-full border text-xs font-medium bg-yellow-500/20 text-yellow-300 border-yellow-500/50">
                  {t('biddingRevision.badgePending') || 'Pending review'}
                </div>
              )}
              {/* Approval Status (skip draft if video status badge is already shown) */}
              {bidding.status && (bidding.status !== 'draft' || !bidding.videoStatus) && (
                <div className={`px-3 py-1 rounded-full border text-xs font-medium flex items-center gap-1 ${
                  bidding.status === 'draft' ? 'bg-gray-600/20 text-gray-400 border-gray-500/50' :
                  bidding.status === 'approved' ? 'bg-green-500/20 text-green-400 border-green-500/50' :
                  bidding.status === 'rejected' ? 'bg-red-500/20 text-red-400 border-red-500/50' :
                  bidding.status === 'hidden' ? 'bg-gray-500/20 text-gray-400 border-gray-500/50' :
                  'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
                }`}>
                  {bidding.status === 'hidden' && <ICONS.EyeOff className="w-3 h-3" />}
                  {t(`bidding.statuses.${bidding.status}`) || bidding.status}
                </div>
              )}
            </div>
            );
          })}
        </div>
        
        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            items={getContextMenuItems(contextMenu.bidding)}
            onClose={() => setContextMenu(null)}
          />
        )}
        
        {generalContextMenu && (
          <ContextMenu
            x={generalContextMenu.x}
            y={generalContextMenu.y}
            items={[
              {
                label: t('contextMenu.createBiddingFromVideo'),
                icon: <ICONS.Video className="w-4 h-4" />,
                onClick: () => onCreateBidding('video'),
              },
              {
                label: t('contextMenu.createBiddingManual'),
                icon: <ICONS.Calculator className="w-4 h-4" />,
                onClick: () => onCreateBidding('manual'),
              },
            ]}
            onClose={() => setGeneralContextMenu(null)}
          />
        )}
        
        {/* Total sum display */}
        {selectedBiddingIds.size > 0 && (
          <div className="fixed bottom-6 right-6 bg-indigo-600 text-white px-6 py-4 rounded-lg shadow-xl z-40 flex items-center gap-4">
            <div>
              <div className="text-sm text-indigo-200">{selectedBiddingIds.size} {selectedBiddingIds.size === 1 ? 'bidding sheet' : 'bidding sheets'} selected</div>
              <div className="flex items-center gap-4 mt-1">
                <div className="text-2xl font-bold">
                  {getCurrencySymbol(selectedTotal.currency)}{selectedTotal.total.toLocaleString()}
                </div>
                {selectedTotal.totalShots > 0 && (
                  <div className="text-lg text-indigo-200 flex items-center gap-1">
                    <ICONS.Camera className="w-4 h-4" />
                    <span>{selectedTotal.totalShots} {selectedTotal.totalShots === 1 ? t('bidding.shotUnit') : t('bidding.shotsUnit')}</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedBiddingIds(new Set());
                setLastSelectedIndex(null);
              }}
              className="text-indigo-200 hover:text-white transition-colors"
              title="Clear selection (Esc)"
            >
              <ICONS.X className="w-5 h-5" />
            </button>
          </div>
        )}
      </>
    );
  }

  // Grid view
  return (
    <>
      <div 
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 p-6"
        onContextMenu={handleGeneralContextMenu}
      >
        {biddingSheets.map((bidding, index) => {
          const isSelected = selectedBiddingIds.has(bidding.id);
          const isHidden = bidding.status === 'hidden';
          return (
          <div
            key={bidding.id}
            data-bidding-tile="true"
            className={`bg-gray-800/50 border rounded-lg p-4 transition-all cursor-pointer relative flex flex-col ${
              isSelected 
                ? 'border-indigo-500 bg-indigo-900/20 ring-2 ring-indigo-500/50' 
                : 'border-gray-700 hover:border-indigo-500/50'
            } ${isHidden ? 'opacity-40 grayscale hover:opacity-70 hover:grayscale-0' : ''}`}
            onClick={(e) => handleBiddingClick(e, bidding, index)}
            onDoubleClick={() => handleBiddingDoubleClick(bidding)}
            onContextMenu={(e) => handleContextMenu(e, bidding)}
          >
            {/* Header */}
            <div className="mb-3">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-semibold text-white mb-1 truncate flex-1 pr-2">
                {bidding.name}
              </h3>
                <div className="flex flex-col gap-1 items-end">
                  {bidding.videoStatus && (!bidding.status || bidding.status === 'draft') && (
                    <div className={`px-2 py-0.5 rounded text-[10px] font-medium ${getStatusColor(bidding.videoStatus)}`}>
                      {t(`bidding.status.${bidding.videoStatus}`) || bidding.videoStatus}
                    </div>
                  )}
                  {/* Approval Status (skip draft if video status badge is already shown) */}
                  {bidding.status && (bidding.status !== 'draft' || !bidding.videoStatus) && (
                    <div className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                      bidding.status === 'draft' ? 'bg-gray-600/20 text-gray-400 border border-gray-500/30' :
                      bidding.status === 'approved' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                      bidding.status === 'rejected' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                      bidding.status === 'sent' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                      bidding.status === 'hidden' ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' :
                      'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    }`}>
                      {bidding.status === 'hidden' && <ICONS.EyeOff className="inline w-3 h-3 mr-1" />}
                      {t(`bidding.statuses.${bidding.status}`) || bidding.status}
                    </div>
                  )}
                </div>
              </div>
              {bidding.projectName && (
                <p className="text-sm text-gray-400 truncate">
                  <ICONS.Briefcase className="inline w-3 h-3 mr-1" />
                  {bidding.projectName}
                </p>
              )}
            </div>

            {/* Thumbnails Preview or Placeholder */}
            <div className="mb-3 aspect-video bg-gray-900 rounded-lg overflow-hidden">
              {bidding.thumbnails && bidding.thumbnails.length > 0 ? (
                <div className="grid grid-cols-3 grid-rows-2 w-full h-full gap-0.5 p-0.5">
                  {bidding.thumbnails.slice(0, 6).map((thumbnail, idx) => (
                    <div key={idx} className="relative bg-gray-800">
                      <img
                        src={thumbnail.startsWith('http') ? thumbnail : `${API_CONFIG.BASE_URL}${thumbnail}`}
                        alt={`Shot ${idx + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}
                  {/* Fill empty slots if less than 6 thumbnails */}
                  {Array.from({ length: Math.max(0, 6 - bidding.thumbnails.length) }).map((_, idx) => (
                    <div key={`empty-${idx}`} className="bg-gray-800 flex items-center justify-center">
                      <ICONS.Camera className="w-6 h-6 text-gray-700" />
                    </div>
                  ))}
                </div>
              ) : bidding.videoUrl ? (
                <div className="relative w-full h-full">
                  <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                    <ICONS.Play className="w-12 h-12 text-gray-600" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-black/60 rounded-full p-3">
                      <ICONS.Video className="w-8 h-8 text-white/80" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ICONS.Video className="w-12 h-12 text-gray-700" />
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1 text-gray-400">
                  <ICONS.Camera className="w-4 h-4" />
                  <span>{bidding.shotCount || 0}</span>
                </div>
                {(() => {
                  const grandTotal = calculateGrandTotal(bidding);
                  if (grandTotal > 0) {
                    return (
                      <div className="flex items-center space-x-1 text-green-400">
                        <ICONS.CreditCard className="w-4 h-4" />
                        <span>{getCurrencySymbol(bidding.currency)}{grandTotal.toLocaleString()}</span>
                      </div>
                    );
                  }
                  if (bidding.currency) {
                    return (
                      <div className="flex items-center space-x-1 text-gray-400">
                        <ICONS.CreditCard className="w-4 h-4" />
                        <span>{bidding.currency}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            {/* Financial Health Bar - ONLY for Approved Sheets */}
            {bidding.status === 'approved' && (
              <div className="mt-3 pt-3 border-t border-gray-700">
                {(() => {
                  // Use grand total from bidding sheet as source of truth (matches finance view)
                  const budget = calculateGrandTotal(bidding);
                  const prepaymentValue = Number(bidding.projectPrepaymentValue) || 0;
                  const totalPaid = Number(bidding.projectTotalPaid) || 0;
                  const hasPrepayment = bidding.projectPrepaymentType && prepaymentValue > 0;
                  
                  // Calculate percentages
                  const paidPercent = budget > 0 ? Math.min((totalPaid / budget) * 100, 100) : 0;
                  const prepaymentThreshold = hasPrepayment 
                    ? (bidding.projectPrepaymentType === 'PERCENT' 
                        ? prepaymentValue 
                        : (budget > 0 ? (prepaymentValue / budget) * 100 : 0))
                    : 0;
                  const prepaymentMet = hasPrepayment 
                    ? (bidding.projectPrepaymentType === 'PERCENT' 
                        ? paidPercent >= prepaymentValue
                        : totalPaid >= prepaymentValue)
                    : true;
                  
                  return (
                    <>
                      {/* Labels row */}
                      <div className="flex justify-between text-xs mb-1">
                        <span className={paidPercent > 0 ? 'text-blue-400' : 'text-gray-400'}>
                          {t('transactions.incomeProgress') || 'Зачисления'}: {paidPercent.toFixed(0)}%
                        </span>
                        {hasPrepayment && (
                          <span className={prepaymentMet ? 'text-green-400' : 'text-orange-400'}>
                            {t('prepayment.prepaymentLabel') || 'Предоплата'}: {prepaymentThreshold.toFixed(0)}%
                          </span>
                        )}
                      </div>
                      
                      {/* Progress bar */}
                      <div className="relative w-full bg-gray-700 rounded-full h-2">
                        {/* Prepayment threshold marker */}
                        {hasPrepayment && (
                          <div 
                            className="absolute top-0 bottom-0 w-0.5 bg-yellow-400 z-10"
                            style={{ left: `${Math.min(prepaymentThreshold, 100)}%` }}
                            title={`${t('prepayment.threshold') || 'Порог предоплаты'}: ${prepaymentThreshold.toFixed(0)}%`}
                          />
                        )}
                        {/* Income progress - BLUE to match finance view */}
                        <div 
                          className={`h-2 rounded-full transition-all ${
                            hasPrepayment 
                              ? (prepaymentMet ? 'bg-blue-500' : 'bg-blue-400')
                              : 'bg-blue-500'
                          }`}
                          style={{ width: `${paidPercent}%` }}
                          title={`${t('transactions.received') || 'Получено'}: ${getCurrencySymbol(bidding.currency || 'USD')}${totalPaid.toLocaleString()}`}
                        />
                      </div>
                      
                      {/* Status message */}
                      {hasPrepayment && !prepaymentMet && (
                        <div className="text-xs text-orange-400 mt-1">
                          ⚠️ {t('prepayment.checkTransactions') || 'Требуется предоплата'}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Spacer to push footer to bottom */}
            <div className="flex-grow" />

            {/* Footer - always at bottom */}
            {(bidding.createdAt || bidding.createdByName) && (
              <div className="mt-3 pt-3 border-t border-gray-700 text-xs text-gray-500">
                  {bidding.createdAt && (
                  <div className="mb-0.5">
                      {t('common.created')}: {new Date(bidding.createdAt).toLocaleDateString()}
                  </div>
                  )}
                  {bidding.createdByName && (
                  <div className="flex items-center space-x-1">
                      <ICONS.User className="w-3 h-3" />
                      <span>{bidding.createdByName}</span>
                  </div>
                  )}
              </div>
            )}
          </div>
          );
        })}
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={getContextMenuItems(contextMenu.bidding)}
          onClose={() => setContextMenu(null)}
        />
      )}

      {generalContextMenu && (
        <ContextMenu
          x={generalContextMenu.x}
          y={generalContextMenu.y}
          items={[
            {
              label: t('contextMenu.createBiddingFromVideo'),
              icon: <ICONS.Video className="w-4 h-4" />,
              onClick: () => onCreateBidding('video'),
            },
            {
              label: t('contextMenu.createBiddingManual'),
              icon: <ICONS.Calculator className="w-4 h-4" />,
              onClick: () => onCreateBidding('manual'),
            },
          ]}
          onClose={() => setGeneralContextMenu(null)}
        />
      )}
      
      {/* Total sum display */}
      {selectedBiddingIds.size > 0 && (
        <div className="fixed bottom-6 right-6 bg-indigo-600 text-white px-6 py-4 rounded-lg shadow-xl z-40 flex items-center gap-4">
          <div>
            <div className="text-sm text-indigo-200">{selectedBiddingIds.size} {selectedBiddingIds.size === 1 ? 'bidding sheet' : 'bidding sheets'} selected</div>
            <div className="text-2xl font-bold">
              {getCurrencySymbol(selectedTotal.currency)}{selectedTotal.total.toLocaleString()}
            </div>
          </div>
          <button
            onClick={() => {
              setSelectedBiddingIds(new Set());
              setLastSelectedIndex(null);
            }}
            className="text-indigo-200 hover:text-white transition-colors"
            title="Clear selection (Esc)"
          >
            <ICONS.X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Rename Modal */}
      <RenameBiddingModal
        isOpen={renameModal.isOpen}
        currentName={renameModal.currentName}
        onClose={() => setRenameModal({ isOpen: false, biddingId: '', currentName: '' })}
        onRename={(newName) => {
          onRenameBidding(renameModal.biddingId, newName);
        }}
      />
    </>
  );
};

export default BiddingList;

